import React, { useEffect, useRef, useState } from 'react'
import { useExclusiveMenu } from '../../hooks/useExclusiveMenu'
import { FormControl } from '@mui/material'
import Select, { MultiValue } from 'react-select'
import { SelectChangeEvent } from '@mui/material/Select'
import { incyiaSelectStyles } from '../../utils/incyiaSelectStyles'
import { useTranslation } from 'react-i18next'
import { getIncidentsByMissionIdApi } from '../../api/incidentApi'
import { useAsyncOptions } from '../../hooks/useAsyncOptions'
import { createOptionProps } from '../../components/common/CreateOptionMenu'
import { Incident } from '../../types/incidentTypes'

interface SelectIncidentProps {
  value: string[]
  onChange: (event: SelectChangeEvent<string[]>) => void
  MissionId: string
  disablePortalMenu?: boolean
  onCreateNew?: (search: string) => void
  reloadKey?: number
}

const SelectIncident: React.FC<SelectIncidentProps> = ({
  value,
  onChange,
  MissionId,
  disablePortalMenu = false,
  onCreateNew,
  reloadKey = 0,
}) => {
  const menu = useExclusiveMenu(!disablePortalMenu)
  const { t } = useTranslation()
  const { data: incidents, status, retry } = useAsyncOptions(
    async () => {
      const response = await getIncidentsByMissionIdApi(MissionId)
      return incidentsFromPayload(response.data)
    },
    [MissionId, reloadKey],
    Boolean(MissionId)
  )

  const containerRef = useRef<HTMLDivElement | null>(null)


  const options =
    status === 'ready'
      ? (incidents ?? []).map((incident) => ({
          value: incident._id || '',
          label: incident.IncidentName || '',
        }))
      : []

  const selectedOptions = options.filter((opt) => value?.includes(opt.value))

  const handleSelectChange = (
    selected: MultiValue<{ value: string; label: string }>
  ) => {
    onChange({
      target: { name: 'IncidentId', value: selected ? selected.map((s) => s.value) : [] },
    } as SelectChangeEvent<string[]>)
  }

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <FormControl fullWidth style={{ width: '100%' }}>
        <Select
          isMulti
          value={selectedOptions}
          onChange={handleSelectChange}
          options={options}
          isLoading={status === 'loading'}
          isDisabled={status === 'loading' || !MissionId}
          placeholder={
            !MissionId
              ? t('actionImport.context.pickMissionFirst')
              : status === 'loading'
                ? t('selectOptions.loading')
                : t('incidents')
          }
          noOptionsMessage={() =>
            !MissionId
              ? t('actionImport.context.pickMissionFirst')
              : status === 'error'
                ? t('selectOptions.failed')
                : t('tables.noData')
          }
          {...menu}
          styles={incyiaSelectStyles}
          {...createOptionProps(
            onCreateNew && MissionId
              ? { label: t('quickCreate.new.incident'), onCreate: onCreateNew }
              : undefined
          )}
        />
      </FormControl>
      {status === 'error' ? (
        <button
          type="button"
          onClick={retry}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#076B5C',
            cursor: 'pointer',
            fontFamily: 'Lato, sans-serif',
            fontSize: 13,
          }}
        >
          {t('selectOptions.retry')}
        </button>
      ) : null}
    </div>
  )
}

function incidentsFromPayload(payload: unknown): Incident[] {
  if (Array.isArray(payload)) return payload as Incident[]
  if (!payload || typeof payload !== 'object') return []
  const record = payload as Record<string, unknown>
  if (Array.isArray(record.incidents)) return record.incidents as Incident[]
  return Object.values(record).filter((item): item is Incident => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false
    return 'IncidentName' in item || '_id' in item
  })
}

export default SelectIncident
